import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import type { PropsWithChildren } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useEntitySecurityPath } from '../../auth/Security';
import { useDeepLinkInitialState } from './hooks/useDeepLinkInitialState';

type DeepLinkItemHighlightProps = PropsWithChildren<{
    id: string;
    deepLinkUIStatePath?: string;
}>;

const HIGHLIGHT_ANIMATION_DURATION_MS = 2000;

/**
 * @todo
 * Replace hard coded color values with values from theme
 */
const deepLinkItemHighlightFade = keyframes`
    from {
        background-color: rgba(255, 214, 102, 0.65);
    }
    to {
        background-color: rgba(255, 214, 102, 0);
    }
`;

export default function DeepLinkItemHighlight({ id, deepLinkUIStatePath, children }: DeepLinkItemHighlightProps) {
    const entitySecurityPath = useEntitySecurityPath();

    const { getDeepLinkInitialState, pluckDeepLinkInitialState } = useDeepLinkInitialState();
    const highlightContainerRef = useRef<HTMLDivElement>(null);

    const hasResolvedInitialHighlightRef = useRef(false);
    const [shouldHighlight, setShouldHighlight] = useState(false);

    useEffect(() => {
        if (hasResolvedInitialHighlightRef.current) {
            return;
        }

        const baseDeepLinkUIStatePath = deepLinkUIStatePath ?? entitySecurityPath;
        if (!baseDeepLinkUIStatePath) {
            return;
        }

        hasResolvedInitialHighlightRef.current = true;

        const itemIdDeepLinkUIStatePath = `${baseDeepLinkUIStatePath}.id`;

        const maybeDeepLinkedItemId = getDeepLinkInitialState<string | undefined>(itemIdDeepLinkUIStatePath);

        if (maybeDeepLinkedItemId === id) {
            pluckDeepLinkInitialState(itemIdDeepLinkUIStatePath);

            setShouldHighlight(true);

            requestAnimationFrame(() => {
                highlightContainerRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            });
        }
    }, [deepLinkUIStatePath, entitySecurityPath, getDeepLinkInitialState, id, pluckDeepLinkInitialState]);

    return (
        <Box
            ref={highlightContainerRef}
            sx={{
                borderRadius: 1,
                animation: shouldHighlight
                    ? `${deepLinkItemHighlightFade} ${HIGHLIGHT_ANIMATION_DURATION_MS}ms ease-out forwards`
                    : undefined
            }}
            onAnimationEnd={(event) => {
                if (event.target !== event.currentTarget) {
                    return;
                }

                setShouldHighlight(false);
            }}
        >
            {children}
        </Box>
    );
}
